import React, { useState, useRef, useEffect, useCallback } from 'react';

const projectData = [
    {
        title: 'Zen Athlete',
        description: 'An activity recovery app designed to help athletes track and optimize their recovery process after workouts and training sessions.',
        tags: ['React Native', 'Firebase', 'JavaScript'],
        link: 'https://github.com/joshuaj0421/ZenAthlete',
    },
    {
        title: 'DoppelHanger',
        description: 'An intelligent app that analyzes your existing outfits to recommend new clothing items that complement your current style.',
        tags: ['React Native', 'Pytorch', 'Flask'],
        link: 'https://github.com/pcomer04/HackMidwest-DoppelHanger',
    },
    {
        title: 'Finopsis',
        description: 'A comprehensive stock and market analysis tool providing detailed insights and data for investment decision making.',
        tags: ['Python', 'React', 'Fast API'],
        link: 'https://finopsis.net/',
    },
    {
        title: 'Chess AI',
        description: 'An intelligent chess engine with advanced move prediction and game analysis capabilities.',
        tags: ['Python', 'Machine Learning', 'Algorithms'],
        link: 'https://github.com/AMcQueen95/ChessAI',
    },
];

const ProjectCard = ({ project, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const scale = isHovered ? 1.05 : 1;

    return (
        <a
            href={project.link}
            onClick={onClick}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                textDecoration: 'none',
                flex: '0 0 auto',
                width: '330px',
                scrollSnapAlign: 'center',
                transform: `scale(${scale})`,
                transition: 'transform 0.2s ease-in-out',
                userSelect: 'none',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onDragStart={(e) => e.preventDefault()}
        >
            <div
                style={{
                    background: '#1C1C1E',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    height: '180px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    color: 'white',
                }}
            >
                <div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#fff' }}>{project.title}</h3>
                    <p style={{ fontSize: '1rem', color: '#E5E5EA', marginTop: '0.5rem' }}>{project.description}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
                    {project.tags.map((tag, index) => (
                        <span key={index} style={{ background: '#3A3A3C', borderRadius: '8px', padding: '0.3rem 0.75rem', fontSize: '0.9rem', color: '#E5E5EA' }}>
                            {tag}
                        </span>
                    ))}
                </div>
            </div>
        </a>
    );
};

const Projects = () => {
    const carouselRef = useRef(null);
    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const wasDragged = useRef(false);

    const velocity = useRef(0);
    const lastPos = useRef(0);
    const animationFrameId = useRef(null);

    // Center the carousel on the middle set of duplicated projects
    useEffect(() => {
        if (carouselRef.current) {
            const carousel = carouselRef.current;
            const middleCard = carousel.children[5]; // 6th item (index 5) - middle of second set
            if (middleCard) {
                const cardWidth = middleCard.offsetWidth;
                const carouselWidth = carousel.offsetWidth;
                const centerPosition = middleCard.offsetLeft + cardWidth / 2 - carouselWidth / 2;
                carousel.scrollLeft = centerPosition;
            }
        }
    }, []);

    const snapToCenter = useCallback(() => {
        const carousel = carouselRef.current;
        if (!carousel || !carousel.children.length) return;

        cancelAnimationFrame(animationFrameId.current);

        const cards = Array.from(carousel.children);
        const carouselCenter = carousel.scrollLeft + carousel.offsetWidth / 2;

        let closestCardIndex = -1;
        let minDistance = Infinity;

        cards.forEach((card, index) => {
            const cardCenter = card.offsetLeft + card.offsetWidth / 2;
            const distance = Math.abs(carouselCenter - cardCenter);
            if (distance < minDistance) {
                minDistance = distance;
                closestCardIndex = index;
            }
        });

        if (closestCardIndex === -1) return;

        const targetCard = cards[closestCardIndex];
        const targetScrollLeft = targetCard.offsetLeft + targetCard.offsetWidth / 2 - carousel.offsetWidth / 2;

        const startTime = performance.now();
        const startScroll = carousel.scrollLeft;
        const duration = 400; // ms for snap animation

        const animateScroll = (currentTime) => {
            const elapsedTime = currentTime - startTime;
            if (elapsedTime >= duration) {
                carousel.scrollLeft = targetScrollLeft;
                carousel.style.scrollSnapType = 'x mandatory';
                return;
            }

            const ease = 1 - Math.pow(1 - (elapsedTime / duration), 4); // easeOutQuart
            carousel.scrollLeft = startScroll + (targetScrollLeft - startScroll) * ease;

            animationFrameId.current = requestAnimationFrame(animateScroll);
        };

        animationFrameId.current = requestAnimationFrame(animateScroll);
    }, []);

    const momentumScroll = useCallback(() => {
        if (Math.abs(velocity.current) < 0.5) {
            snapToCenter();
            return;
        }

        const carousel = carouselRef.current;
        const newScrollLeft = carousel.scrollLeft + velocity.current;

        // Check for infinite scroll
        if (newScrollLeft <= 0) {
            // Reached the beginning, jump to the end
            carousel.scrollLeft = carousel.scrollWidth - carousel.offsetWidth;
        } else if (newScrollLeft >= carousel.scrollWidth - carousel.offsetWidth) {
            // Reached the end, jump to the beginning
            carousel.scrollLeft = 0;
        } else {
            carousel.scrollLeft = newScrollLeft;
        }

        velocity.current *= 0.95; // Friction
        animationFrameId.current = requestAnimationFrame(momentumScroll);
    }, [snapToCenter]);

    const handleMouseDown = useCallback((e) => {
        cancelAnimationFrame(animationFrameId.current);
        isDown.current = true;
        startX.current = e.pageX - carouselRef.current.offsetLeft;
        scrollLeft.current = carouselRef.current.scrollLeft;
        wasDragged.current = false;
        velocity.current = 0;
        lastPos.current = e.pageX;
        carouselRef.current.style.cursor = 'grabbing';
        carouselRef.current.style.scrollSnapType = 'none';
    }, []);

    const handleMouseUp = useCallback(() => {
        isDown.current = false;
        carouselRef.current.style.cursor = 'grab';
        if (wasDragged.current) {
            momentumScroll();
        } else {
            snapToCenter();
        }
    }, [momentumScroll, snapToCenter]);

    const handleMouseMove = useCallback((e) => {
        if (!isDown.current) return;
        e.preventDefault();

        const x = e.pageX - carouselRef.current.offsetLeft;
        const walk = x - startX.current;
        if (!wasDragged.current && Math.abs(walk) > 10) {
            wasDragged.current = true;
        }
        if (wasDragged.current) {
            carouselRef.current.scrollLeft = scrollLeft.current - walk;
            velocity.current = e.pageX - lastPos.current;
            lastPos.current = e.pageX;
        }
    }, []);

    useEffect(() => {
        const handleMouseLeave = () => {
            if (isDown.current) {
                handleMouseUp();
            }
        };

        const currentCarousel = carouselRef.current;
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousemove', handleMouseMove);
        currentCarousel?.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousemove', handleMouseMove);
            currentCarousel?.removeEventListener('mouseleave', handleMouseLeave);
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [handleMouseUp, handleMouseMove]);


    const handleCardClick = (e) => {
        if (wasDragged.current) {
            e.preventDefault();
        }
    };

    return (
        <section id="projects" style={{
            background: '#000',
            color: '#fff',
            padding: '4rem 0',
            width: '100%',
        }}>
            <h2 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '3rem', fontWeight: 'bold' }}>Projects</h2>
            <div
                ref={carouselRef}
                onMouseDown={handleMouseDown}
                style={{
                    display: 'flex',
                    gap: '2rem',
                    width: '90%',
                    margin: '0 auto',
                    maxWidth: '1200px',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollPadding: '0 2rem',
                    padding: '1rem 2rem',
                    scrollbarWidth: 'none',
                    cursor: 'grab',
                    userSelect: 'none',
                }}
            >
                {[...projectData, ...projectData, ...projectData].map((project, index) => (
                    <ProjectCard key={index} project={project} onClick={handleCardClick} />
                ))}
            </div>
        </section>
    );
};

export default Projects; 